/**
 * Utility functions for field naming and formula calculations
 */

/**
 * Generates a valid field name from a label
 * Converts to lowercase, removes special characters, replaces spaces with underscores
 */
export const generateFieldName = (label: string): string => {
    return label
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '') // Remove special characters
        .trim()
        .replace(/\s+/g, '_') // Replace spaces with underscores
        .replace(/_{2,}/g, '_') // Replace multiple underscores with single
        .substring(0, 50); // Limit length
};

/**
 * Validates a field name (must be alphanumeric with underscores only)
 */
export const isValidFieldName = (name: string): boolean => {
    return /^[a-z][a-z0-9_]*$/.test(name);
};

/**
 * Makes a field name unique by appending a number if necessary
 */
export const makeFieldNameUnique = (
    desiredName: string,
    existingNames: string[]
): string => {
    // If the name doesn't exist or is the current field's name, return it
    if (!existingNames.includes(desiredName)) {
        return desiredName;
    }

    // Try appending numbers until we find a unique name
    let counter = 1;
    let uniqueName = `${desiredName}_${counter}`;
    
    while (existingNames.includes(uniqueName)) {
        counter++;
        uniqueName = `${desiredName}_${counter}`;
    }
    
    return uniqueName;
};

/**
 * Gets all field names from form sections
 */
export const getAllFieldNames = (sections: any[]): Map<string, string> => {
    const fieldNames = new Map<string, string>();
    
    sections.forEach(section => {
        section.rows.forEach((row: any) => {
            row.fields.forEach((field: any) => {
                const fieldName = field.config.fieldName || generateFieldName(field.config.label);
                fieldNames.set(field.id, fieldName);
            });
        });
    });
    
    return fieldNames;
};

/**
 * Extracts field references from a formula
 * Example: "price * quantity + tax" -> ["price", "quantity", "tax"]
 */
export const extractFieldReferences = (formula: string): string[] => {
    // Match word tokens that could be field names
    const tokens = formula.match(/[a-z_][a-z0-9_]*/gi) || [];
    
    // Filter out mathematical operators and functions
    const operators = ['and', 'or', 'not', 'if', 'then', 'else', 'true', 'false'];
    const mathFunctions = ['abs', 'ceil', 'floor', 'round', 'sqrt', 'pow', 'min', 'max', 'sum', 'avg'];
    
    return [...new Set(tokens.filter(token => 
        !operators.includes(token.toLowerCase()) && 
        !mathFunctions.includes(token.toLowerCase())
    ))];
};

/**
 * Validates a formula and returns any errors
 */
export const validateFormula = (
    formula: string,
    availableFields: Map<string, string>
): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    if (!formula || formula.trim() === '') {
        return { valid: true, errors: [] };
    }
    
    // Extract field references
    const fieldRefs = extractFieldReferences(formula);
    
    // Check if all referenced fields exist
    const availableFieldNames = Array.from(availableFields.values());
    fieldRefs.forEach(fieldRef => {
        if (!availableFieldNames.includes(fieldRef)) {
            errors.push(`Field "${fieldRef}" not found`);
        }
    });
    
    // Basic syntax check
    const invalidChars = formula.match(/[^a-z0-9_+\-*/().%\s]/gi);
    if (invalidChars && invalidChars.length > 0) {
        errors.push(`Invalid characters: ${invalidChars.join(', ')}`);
    }
    
    // Check for balanced parentheses
    const openCount = (formula.match(/\(/g) || []).length;
    const closeCount = (formula.match(/\)/g) || []).length;
    if (openCount !== closeCount) {
        errors.push('Unbalanced parentheses');
    }
    
    return {
        valid: errors.length === 0,
        errors
    };
};

/**
 * Gets a human-readable description of a formula with field labels
 */
export const getFormulaDescription = (
    formula: string,
    fieldNames: Map<string, string>,
    fieldLabels: Map<string, string>
): string => {
    let description = formula;
    
    fieldNames.forEach((name, id) => {
        const label = fieldLabels.get(id) || name;
        const regex = new RegExp(`\\b${name}\\b`, 'g');
        description = description.replace(regex, `[${label}]`);
    });
    
    return description;
};
